import React from "react"
import { twMerge } from "tailwind-merge"
import Typography from "./Typography"
import {
  FaBuilding,
  FaUsers,
  FaUserTie,
  FaSitemap,
  FaProjectDiagram,
  FaNetworkWired,
  FaHome,
  FaChartBar,
  FaUniversity,
  FaHandshake,
} from "react-icons/fa"
import {
  MdBusiness,
  MdGroups,
  MdAccountTree,
  MdDomain,
  MdWork,
} from "react-icons/md"
import {
  HiBuildingOffice,
  HiBuildingLibrary,
  HiUsers,
  HiUserGroup,
} from "react-icons/hi2"
import { LuBuilding2, LuNetwork, LuBriefcase } from "react-icons/lu"

// Types
interface OrganizationIcon {
  name: string
  component: any
}

interface OrganizationIconCardProps {
  bg: string
  name: string
  zIndex: number
  icon: string
  organizationId: string
  width?: number
  height?: number
  className?: string
}

// Icon constants
const ORGANIZATION_ICONS: OrganizationIcon[] = [
  // Font Awesome
  { name: "Building", component: FaBuilding },
  { name: "Users", component: FaUsers },
  { name: "UserTie", component: FaUserTie },
  { name: "Sitemap", component: FaSitemap },
  { name: "ProjectDiagram", component: FaProjectDiagram },
  { name: "NetworkWired", component: FaNetworkWired },
  { name: "Home", component: FaHome },
  { name: "ChartBar", component: FaChartBar },
  { name: "University", component: FaUniversity },
  { name: "Handshake", component: FaHandshake },
  // Material Design
  { name: "Business", component: MdBusiness },
  { name: "Groups", component: MdGroups },
  { name: "AccountTree", component: MdAccountTree },
  { name: "Domain", component: MdDomain },
  { name: "Work", component: MdWork },
  // HeroIcons
  { name: "BuildingOffice", component: HiBuildingOffice },
  { name: "BuildingLibrary", component: HiBuildingLibrary },
  { name: "Users2", component: HiUsers },
  { name: "UserGroup", component: HiUserGroup },
  // Lucide
  { name: "Building2", component: LuBuilding2 },
  { name: "Network", component: LuNetwork },
  { name: "Briefcase", component: LuBriefcase },
]

// Helper function
export const getIconByName = (name: string): OrganizationIcon | undefined => {
  return ORGANIZATION_ICONS.find((icon) => icon.name === name)
}

// Constants
const DEFAULT_SIZE = 40
const CARD_BASE_CLASSES =
  "rounded-[8px] flex items-center justify-center cursor-pointer"
const ICON_COLOR = "#FFFFFF"
const TEXT_SIZE = "text-[20px]"

// Helper function to get dynamic size classes
const getCardSizeClasses = (width: number, height: number): string => {
  return `w-[${width}px] min-w-[${width}px] h-[${height}px] min-h-[${height}px]`
}

// Helper function to calculate icon size based on card size
const getIconSize = (cardSize: number): number => {
  return Math.round(cardSize * 0.625) // 62.5% of card size
}

// Helper function to get merged className
const getMergedClassName = (
  sizeClasses: string,
  customClassName?: string
): string => {
  if (customClassName) {
    return twMerge(sizeClasses, customClassName || CARD_BASE_CLASSES)
  }
  return twMerge(sizeClasses, CARD_BASE_CLASSES)
}

// Helper functions
const isUploadedFile = (icon: string, organizationId: string): boolean => {
  return icon.includes(organizationId)
}

const getInitialLetter = (name: string): string => {
  return name.charAt(0).toUpperCase()
}

const renderUploadedImage = (
  icon: string,
  zIndex: number,
  width: number,
  height: number,
  className?: string
) => (
  <div
    className={getMergedClassName(getCardSizeClasses(width, height), className)}
    style={{
      zIndex,
      background: `url(${icon}) no-repeat center center`,
      backgroundSize: "cover",
      backgroundColor: "#F7F7F7",
    }}
  />
)

const renderIconComponent = (
  iconName: string,
  bg: string,
  zIndex: number,
  width: number,
  height: number,
  className?: string
) => {
  const iconConfig = getIconByName(iconName)

  if (!iconConfig) {
    return null
  }

  const iconSize = getIconSize(Math.min(width, height))
  const sizeClasses = getCardSizeClasses(width, height)
  const baseClasses = className
    ? twMerge(sizeClasses, className || CARD_BASE_CLASSES)
    : twMerge(sizeClasses, CARD_BASE_CLASSES)

  return (
    <div
      className={twMerge(baseClasses, "z-10")}
      style={{
        zIndex,
        background: bg || undefined,
      }}
    >
      {React.createElement(iconConfig.component, {
        size: iconSize,
        color: ICON_COLOR,
      })}
    </div>
  )
}

const renderInitialLetter = (
  name: string,
  bg: string,
  zIndex: number,
  width: number,
  height: number,
  className?: string
) => {
  const sizeClasses = getCardSizeClasses(width, height)
  const baseClasses = className
    ? twMerge(sizeClasses, className || CARD_BASE_CLASSES)
    : twMerge(sizeClasses, CARD_BASE_CLASSES)

  return (
    <div
      className={twMerge(baseClasses, "z-10", !bg && "square")}
      style={{
        zIndex,
        background: bg || undefined,
      }}
    >
      <Typography className={`text-white ${TEXT_SIZE} font-semibold`}>
        {getInitialLetter(name)}
      </Typography>
    </div>
  )
}

export const OrganizationIconCard: React.FC<OrganizationIconCardProps> = ({
  bg,
  name,
  zIndex,
  icon,
  organizationId,
  width = DEFAULT_SIZE,
  height = DEFAULT_SIZE,
  className,
}) => {
  // No icon provided - show initial letter
  if (!icon) {
    return renderInitialLetter(name, bg, zIndex, width, height, className)
  }

  // Check if it's an uploaded file
  if (isUploadedFile(icon, organizationId)) {
    return renderUploadedImage(icon, zIndex, width, height, className)
  }
  // Render predefined icon
  return renderIconComponent(icon, bg, zIndex, width, height, className)
}
